import { settings } from '../config';
import { HandoverReportDetails, HandoverReportFilter, HandoverReportListItem } from '../types/handover';
import { getToken } from '../global/TokenValidator';

type MessageResult = { status: boolean, message: string };
type ListResult = { status: boolean, message: string, data: Array<HandoverReportListItem>, total: number };
type DetailsResult = { status: boolean, message: string, data: HandoverReportDetails };
type CreateResult = { status: boolean, message: string, data: number };

type HandoverApiRoutes = {
    /** URL prefix, e.g. "LinePlannerHandOver". */
    basePath: string;
    /** Infix the per-report routes are named after, e.g. "LinePlanner". */
    reportName: string;
};

/**
 * Builds a client for one of the report-style handover endpoint families. Line Planner and
 * Maintenance Control expose the same surface under different path prefixes and permission groups,
 * so only the route naming varies.
 */
export const createHandoverApi = ({ basePath, reportName }: HandoverApiRoutes) => {

    const request = async <T,>(path: string, method: string, body?: object): Promise<T> => {
        const response = await fetch(`${settings.HOST}/${basePath}/${path}`, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'apikey': settings.KONG_KEY,
                'token': await getToken(),
            },
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        });
        return response.json();
    };

    return {
        GetAll: (): Promise<ListResult> =>
            request(`GetAll${reportName}HandoverReports`, 'GET'),

        GetById: (reportId: number): Promise<DetailsResult> =>
            request(`Get${reportName}HandoverReportByID/${reportId}`, 'GET'),

        // Both controllers name this route identically, with no report infix.
        Search: (filter: HandoverReportFilter): Promise<ListResult> =>
            request('GetHandoverBySearchFilters', 'POST', filter),

        Create: (): Promise<CreateResult> =>
            request(`CreateNew${reportName}HandoverReport`, 'POST', {}),

        Close: (reportId: number): Promise<MessageResult> =>
            request(`Close${reportName}HandoverReport/${reportId}`, 'PUT'),

        AddIssue: (reportId: number, issue: string, tailNumber: string): Promise<MessageResult> =>
            request('AddIssue', 'POST', { report_id: reportId, issue, tail_number: tailNumber }),

        // report_id is sent so the backend can verify the issue belongs to the report being edited.
        UpdateIssue: (issueId: number, reportId: number, issue: string, tailNumber: string): Promise<MessageResult> =>
            request('UpdateIssue', 'PUT', { id: issueId, report_id: reportId, issue, tail_number: tailNumber }),

        DeleteIssue: (issueId: number, reportId: number): Promise<MessageResult> =>
            request('DeleteIssue', 'DELETE', { id: issueId, report_id: reportId }),

        AddLabour: (reportId: number, employeeId: number): Promise<MessageResult> =>
            request(`AddLabour/${reportId}`, 'POST', { user_id: employeeId }),

        DeleteLabour: (reportId: number, employeeId: number): Promise<MessageResult> =>
            request(`DeleteLabour/${reportId}`, 'DELETE', { user_id: employeeId }),

        AddToBeReadBy: (reportId: number, userId: number): Promise<MessageResult> =>
            request('AddToBeReadBy', 'POST', { report_id: reportId, to_be_read_by: userId }),

        SaveGeneralNotes: (reportId: number, generalNotes: string): Promise<MessageResult> =>
            request('SaveGeneralNotes', 'POST', { report_id: reportId, general_notes: generalNotes }),

        ChangeHandoverDayNight: (reportId: number, isDay: boolean): Promise<MessageResult> =>
            request('ChangeHandoverDayNight', 'POST', { report_id: reportId, is_day: isDay }),

        /**
         * Emails the report to the department address configured on the backend. Only the Line
         * Planner controller implements this route; the `showEmailReport` config flag is the gate.
         */
        EmailReport: (reportId: number): Promise<MessageResult> =>
            request(`Email${reportName}HandoverReport/${reportId}`, 'POST'),
    };
};

export type HandoverApi = ReturnType<typeof createHandoverApi>;
